import { useRef, useState } from "react"
import { parsePhoneNumber } from "react-phone-number-input/input"

import {
  type BaseUserFragment,
  useAuthV2Mutation,
  useRequestPhoneVerificationMutation,
} from "../generated/graphql"
import { graphQLClient } from "../graphql/client"
import { processException } from "./errors"

interface PhoneAuthResult {
  token: string
  user: BaseUserFragment | undefined
  communitiesWhereMember: any[]
}

export function usePhoneAuth() {
  const { mutateAsync: auth } = useAuthV2Mutation(graphQLClient)
  const { mutateAsync: requestPhoneVerification } =
    useRequestPhoneVerificationMutation(graphQLClient)

  const [phoneNumber, setPhoneNumber] = useState("")
  const [showOtpModal, setShowOtpModal] = useState(false)
  const [otpError, setOtpError] = useState("")
  const [isOtpVerifying, setIsOtpVerifying] = useState(false)
  const pendingOtpOpenIdRef = useRef(0)

  const waitForKeyboardDismiss = async () => {
    if (typeof window === "undefined") return

    const activeElement = document.activeElement
    const isActiveTextEntry =
      activeElement instanceof HTMLElement &&
      !!activeElement.closest("input, textarea, [contenteditable='true']")

    if (!isActiveTextEntry) {
      return
    }

    activeElement.blur()

    const isTouchDevice =
      typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches

    if (!isTouchDevice) {
      return
    }

    const visualViewport = window.visualViewport
    if (!visualViewport) {
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 150)
      })
      return
    }

    await new Promise<void>((resolve) => {
      let settleTimeout: number | null = null
      let hardTimeout: number | null = null

      const cleanup = () => {
        if (settleTimeout !== null) {
          window.clearTimeout(settleTimeout)
        }
        if (hardTimeout !== null) {
          window.clearTimeout(hardTimeout)
        }
        visualViewport.removeEventListener("resize", scheduleResolve)
        visualViewport.removeEventListener("scroll", scheduleResolve)
        resolve()
      }

      const scheduleResolve = () => {
        if (settleTimeout !== null) {
          window.clearTimeout(settleTimeout)
        }
        settleTimeout = window.setTimeout(cleanup, 140)
      }

      visualViewport.addEventListener("resize", scheduleResolve)
      visualViewport.addEventListener("scroll", scheduleResolve)
      hardTimeout = window.setTimeout(cleanup, 450)
      scheduleResolve()
    })
  }

  const openOtp = (phone: string) => {
    pendingOtpOpenIdRef.current += 1
    setPhoneNumber(phone)
    setShowOtpModal(true)
    setOtpError("")
  }

  const openOtpAfterKeyboardDismiss = async (phone: string) => {
    const pendingOpenId = pendingOtpOpenIdRef.current + 1
    pendingOtpOpenIdRef.current = pendingOpenId
    setPhoneNumber(phone)
    setOtpError("")

    // Opening the OTP modal while the phone input is still dismissing the mobile keyboard
    // makes the modal "chase" the visual viewport. Wait briefly for the viewport to settle
    // so the transition from phone entry to OTP feels smoother on touch devices.
    await waitForKeyboardDismiss()

    if (pendingOtpOpenIdRef.current !== pendingOpenId) {
      return
    }

    setShowOtpModal(true)
  }

  const submitPhone = async (phone: string) => {
    const parsed = parsePhoneNumber(phone)
    if (!parsed) throw new Error("Invalid phone number")

    const r = await auth({ fromWeb: true, phone: parsed.number })
    if (r.authV2?.verificationCodeSent) {
      setPhoneNumber(phone)
    } else {
      throw new Error("Inconsistent auth state: verificationCodeSent is false")
    }
  }

  const verifyOtp = async (code: string): Promise<PhoneAuthResult> => {
    setIsOtpVerifying(true)
    setOtpError("")
    try {
      const { authV2 } = await auth({
        phone: phoneNumber,
        phoneVerificationCode: code,
      })

      if (!authV2) throw new Error("Auth failed")

      setShowOtpModal(false)
      return {
        token: authV2.token || "",
        user: authV2.user as BaseUserFragment | undefined,
        communitiesWhereMember: authV2.user?.communitiesWhereMember ?? [],
      }
    } catch (err) {
      processException(err)
      setOtpError("Wrong code. Please try again.")
      throw err
    } finally {
      setIsOtpVerifying(false)
    }
  }

  const resendOtp = async (whatsapp?: boolean) => {
    try {
      const r = await requestPhoneVerification({ phone: phoneNumber, whatsapp })
      if (!r.requestPhoneVerification) {
        setOtpError("Failed to resend code")
      }
    } catch (err) {
      processException(err)
      setOtpError("Failed to resend code")
    }
  }

  const closeOtp = () => {
    pendingOtpOpenIdRef.current += 1
    setShowOtpModal(false)
    setPhoneNumber("")
    setOtpError("")
  }

  return {
    phoneNumber,
    showOtpModal,
    otpError,
    isOtpVerifying,
    openOtp,
    openOtpAfterKeyboardDismiss,
    submitPhone,
    verifyOtp,
    resendOtp,
    closeOtp,
  }
}
